// Catálogo de "acciones" del Copiloto: tareas de IA que producen un borrador
// listo para usar. Cada acción arma un prompt que se manda al mismo backend del
// asistente (/api/chat → askAssistant), que ya tiene contexto en vivo + RAG.
//
// Estructura de una acción:
//   id, label, group, icon, desc
//   inputs?: [{ name, label, placeholder, type: 'text'|'textarea'|'select', options?, required? }]
//   related?: { to, label }                 // atajo a la sección relacionada
//   buildPrompt(values) => string           // el turno de usuario que se envía

import {
  FaBullhorn, FaHashtag, FaVideo, FaMicrophoneAlt,
  FaAddressBook, FaReply,
  FaGavel, FaBalanceScale, FaFileAlt,
  FaChessKnight, FaUserFriends, FaCalendarAlt,
} from 'react-icons/fa';

const TONE_OPTIONS = [
  'Cercano y de barrio',
  'Firme y contundente',
  'Institucional y sobrio',
  'Optimista y propositivo',
];

export const ACTION_GROUPS = [
  { id: 'comunicacion', label: 'Comunicación', color: '#f77f00' },
  { id: 'vecinos', label: 'Vecinos', color: '#38b000' },
  { id: 'concejo', label: 'Concejo', color: '#fee440' },
  { id: 'estrategia', label: 'Estrategia', color: '#00b4d8' },
];

export const ACTIONS = [
  /* ---------------- Comunicación ---------------- */
  {
    id: 'comunicado',
    group: 'comunicacion',
    icon: FaBullhorn,
    label: 'Redactar un comunicado',
    desc: 'Gacetilla de prensa lista para enviar a los medios.',
    related: { to: '/prensa', label: 'Motor de Prensa' },
    inputs: [
      { name: 'tema', label: 'Tema del comunicado', placeholder: 'Ej: nueva ordenanza de seguridad en el microcentro', type: 'textarea', required: true },
      { name: 'tono', label: 'Tono', type: 'select', options: TONE_OPTIONS },
    ],
    buildPrompt: ({ tema, tono }) =>
      `Actuá como mi jefe de prensa. Redactá un comunicado de prensa listo para enviar a los medios sobre: "${tema}". Tono: ${tono}. Estructura: título potente, bajada, 3–5 párrafos y una frase mía entre comillas para citar. Español rioplatense, claro. Usá datos reales de la plataforma si aplican; no inventes cifras. Devolvé solo el comunicado.`,
  },
  {
    id: 'post-redes',
    group: 'comunicacion',
    icon: FaHashtag,
    label: 'Post para redes',
    desc: 'Texto para Instagram/Facebook/X con variantes y hashtags.',
    related: { to: '/social', label: 'Redes Sociales' },
    inputs: [
      { name: 'tema', label: '¿Sobre qué querés postear?', placeholder: 'Ej: recorrida por el barrio Química, obras de asfalto', type: 'textarea', required: true },
      { name: 'red', label: 'Red', type: 'select', options: ['Instagram', 'Facebook', 'X (Twitter)', 'Las tres'] },
      { name: 'tono', label: 'Tono', type: 'select', options: TONE_OPTIONS },
    ],
    buildPrompt: ({ tema, red, tono }) =>
      `Actuá como mi community manager. Escribí un posteo para ${red} sobre: "${tema}". Tono: ${tono}. Incluí: texto principal (con buen gancho en la primera línea), 2 variantes más cortas, emojis con mesura y 5–8 hashtags locales relevantes de San Nicolás. Devolvé el post listo para copiar.`,
  },
  {
    id: 'guion-reel',
    group: 'comunicacion',
    icon: FaVideo,
    label: 'Guion para un reel/video',
    desc: 'Guion corto hablado a cámara, con tiempos.',
    inputs: [
      { name: 'tema', label: 'Idea del video', placeholder: 'Ej: explicar en 30 seg por qué voté en contra del aumento de tasas', type: 'textarea', required: true },
      { name: 'duracion', label: 'Duración', type: 'select', options: ['30 segundos', '60 segundos', '90 segundos'] },
    ],
    buildPrompt: ({ tema, duracion }) =>
      `Escribí un guion para un reel de ${duracion} donde hablo a cámara sobre: "${tema}". Formato: bloques con marca de tiempo, texto exacto a decir (frases cortas, habladas, no leídas), y una sugerencia de placa/texto en pantalla por bloque. Cierre con llamado a la acción. Español rioplatense natural.`,
  },
  {
    id: 'discurso',
    group: 'comunicacion',
    icon: FaMicrophoneAlt,
    label: 'Borrador de discurso',
    desc: 'Discurso para un acto o evento, con estructura.',
    inputs: [
      { name: 'ocasion', label: 'Ocasión y público', placeholder: 'Ej: acto por el aniversario del barrio, vecinos y comerciantes', type: 'textarea', required: true },
      { name: 'duracion', label: 'Duración', type: 'select', options: ['3 minutos', '5 minutos', '10 minutos'] },
    ],
    buildPrompt: ({ ocasion, duracion }) =>
      `Escribí un borrador de discurso de ${duracion} para esta ocasión: "${ocasion}". Estructura: apertura que conecte con el público, 2–3 ideas fuerza con ejemplos concretos de San Nicolás, y un cierre emotivo y movilizador. Pensado para ser hablado, no leído. Marcá entre paréntesis dónde hacer pausas o levantar el tono.`,
  },

  /* ---------------- Vecinos ---------------- */
  {
    id: 'responder-reclamo',
    group: 'vecinos',
    icon: FaReply,
    label: 'Responder un reclamo',
    desc: 'Respuesta empática y concreta a un vecino.',
    related: { to: '/vecinos', label: 'Vecinos (CRM)' },
    inputs: [
      { name: 'reclamo', label: '¿Qué plantea el vecino?', placeholder: 'Ej: hace 3 semanas que no pasa el camión de basura por la calle X', type: 'textarea', required: true },
      { name: 'canal', label: 'Canal', type: 'select', options: ['WhatsApp', 'Mail', 'En persona (guion)'] },
    ],
    buildPrompt: ({ reclamo, canal }) =>
      `Un vecino me plantea esto: "${reclamo}". Redactá una respuesta para ${canal}: empática, que muestre que lo escuché, con un próximo paso concreto (a quién derivo o qué gestión hago) y un tono humano, sin sonar a formulario. Breve. Español rioplatense.`,
  },
  {
    id: 'mensaje-vecino',
    group: 'vecinos',
    icon: FaAddressBook,
    label: 'Mensaje a un vecino/grupo',
    desc: 'Mensaje proactivo para difundir una gestión o novedad.',
    inputs: [
      { name: 'novedad', label: 'Novedad a comunicar', placeholder: 'Ej: se aprobó el semáforo que pedían en la esquina de la escuela', type: 'textarea', required: true },
    ],
    buildPrompt: ({ novedad }) =>
      `Redactá un mensaje breve de WhatsApp para mandarle a vecinos contándoles esta novedad conseguida: "${novedad}". Cercano, en primera persona, que se note el trabajo hecho sin ser soberbio. Cerrá invitando a escribirme por lo que necesiten.`,
  },

  /* ---------------- Concejo ---------------- */
  {
    id: 'argumentos',
    group: 'concejo',
    icon: FaBalanceScale,
    label: 'Argumentos sobre un tema',
    desc: 'Argumentos a favor/en contra para debatir con solidez.',
    related: { to: '/labor-hcd', label: 'Mi labor en el HCD' },
    inputs: [
      { name: 'tema', label: 'Tema a debatir', placeholder: 'Ej: proyecto de aumento de tasas municipales', type: 'textarea', required: true },
      { name: 'postura', label: 'Mi postura', type: 'select', options: ['A favor', 'En contra', 'Todavía la estoy definiendo'] },
    ],
    buildPrompt: ({ tema, postura }) =>
      `Preparame para debatir en el Concejo sobre: "${tema}". Mi postura: ${postura}. Dame: 4–5 argumentos sólidos de mi lado (con dato o ejemplo local si hay en la plataforma), las 3 objeciones más probables del otro lado y cómo responder cada una, y una frase de cierre potente. Si mi postura está sin definir, presentá ambos lados con honestidad.`,
  },
  {
    id: 'intervencion',
    group: 'concejo',
    icon: FaGavel,
    label: 'Preparar una intervención',
    desc: 'Guion para hablar en una sesión del Concejo.',
    inputs: [
      { name: 'tema', label: 'Tema de la sesión', placeholder: 'Ej: pedido de informes sobre el estado de las calles', type: 'textarea', required: true },
      { name: 'minutos', label: 'Tiempo de uso de palabra', type: 'select', options: ['2 minutos', '4 minutos', '6 minutos'] },
    ],
    buildPrompt: ({ tema, minutos }) =>
      `Escribí el guion de mi intervención de ${minutos} en la sesión del Concejo sobre: "${tema}". Directa, con datos, respetuosa pero firme. Estructura: planteo del problema, evidencia, propuesta concreta y pedido puntual. Pensada para ser hablada.`,
  },
  {
    id: 'resumen-expediente',
    group: 'concejo',
    icon: FaFileAlt,
    label: 'Explicame un tema del HCD',
    desc: 'Resumen claro de un expediente, ordenanza o tema.',
    related: { to: '/monitoring/hcd', label: 'HCD San Nicolás' },
    inputs: [
      { name: 'tema', label: '¿Qué querés que te explique?', placeholder: 'Ej: de qué se trata el expediente sobre el nuevo código urbano', type: 'textarea', required: true },
    ],
    buildPrompt: ({ tema }) =>
      `Explicame de forma clara y ejecutiva este tema del Concejo de San Nicolás: "${tema}". Usá la información institucional del HCD que tengas en la plataforma. Decime: qué es, en qué estado está, quiénes lo impulsan, a quién beneficia/perjudica y qué debería tener en cuenta yo. Si no hay datos, decilo y sugerí dónde buscar.`,
  },

  /* ---------------- Estrategia ---------------- */
  {
    id: 'resumen-mesa',
    group: 'estrategia',
    icon: FaChessKnight,
    label: 'Resumen para la mesa chica',
    desc: 'El estado de situación del día, ejecutivo.',
    related: { to: '/estrategia', label: 'Sala de Estrategia' },
    inputs: [],
    buildPrompt: () =>
      `Armame un resumen ejecutivo para llevar a la mesa chica hoy, con lo que pasa en la plataforma: reputación, prensa reciente, reclamos de vecinos abiertos, movimientos de competidores y cualquier riesgo u oportunidad. Formato: viñetas cortas por área y, al final, "3 cosas para decidir hoy". No inventes: usá solo datos reales del contexto.`,
  },
  {
    id: 'responder-rival',
    group: 'estrategia',
    icon: FaUserFriends,
    label: 'Cómo responder a un rival',
    desc: 'Estrategia de respuesta a un ataque o declaración.',
    related: { to: '/competitors', label: 'Competencia 360°' },
    inputs: [
      { name: 'situacion', label: '¿Qué dijo o hizo el rival?', placeholder: 'Ej: un concejal opositor me acusó de no presentar proyectos', type: 'textarea', required: true },
    ],
    buildPrompt: ({ situacion }) =>
      `Un rival político hizo esto: "${situacion}". Asesorame: ¿conviene responder o no, y por qué? Si conviene, dame 2 opciones de respuesta (una firme, una elevando el debate) con el texto listo, y decime qué NO hacer. Usá lo que sepas de ese competidor en la plataforma.`,
  },
  {
    id: 'agenda-semana',
    group: 'estrategia',
    icon: FaCalendarAlt,
    label: 'Ideas de agenda de la semana',
    desc: 'Propuestas de actividades y temas para instalar.',
    related: { to: '/planning', label: 'Planificación' },
    inputs: [
      { name: 'foco', label: 'Foco o restricción (opcional)', placeholder: 'Ej: quiero enfocarme en seguridad y en los barrios del sur', type: 'textarea' },
    ],
    buildPrompt: ({ foco }) =>
      `Propone una agenda política para esta semana${foco ? ` con este foco: "${foco}"` : ''}. Dame 5–7 ideas concretas de actividades/temas para instalar, cada una con: qué hacer, por qué ahora (atada a lo que pasa en la plataforma) y qué contenido sacar. Priorizá lo de mayor impacto.`,
  },
];

// Prompts sugeridos para el modo Consultar.
export const QUICK_ASKS = [
  '¿Cómo viene mi reputación esta semana y qué la mueve?',
  '¿Qué reclamos de vecinos tengo abiertos y cuáles son urgentes?',
  '¿Qué están haciendo mis competidores que debería tener en el radar?',
  '¿Qué salió en la prensa local sobre mí o mis temas?',
  'Dame 3 temas para instalar esta semana en San Nicolás',
  '¿Qué expedientes nuevos hay en el Concejo que me convenga mirar?',
];

export function actionById(id) {
  return ACTIONS.find((a) => a.id === id) || null;
}
